import { useVariant } from '@unleash/proxy-client-react';

/**
 * Tunable settings for the diagnostic "I'm Done" early-end feature (TEAMB-3338).
 * Defaults live here (product-signed-off); any key can be overridden per environment at runtime
 * via the Unleash variant {@link DG_END_EARLY_FLAG} — no rebuild/redeploy needed.
 */
export interface DgEndEarlyConfig {
  /** First gate: everyone is checked here, against the `thetaEarly` bar. */
  firstGateQ: number;
  /** Second gate: the bar relaxes to `thetaLate` and everyone still unprompted is re-checked. */
  secondGateQ: number;
  /**
   * Universal auto-submit gate: at/after this question every student is submitted regardless of θ.
   * Defaults to 35 — one past the last question — so it never fires on its own within a 34-item
   * diagnostic; only θ drives an early finish. Lower it (e.g. to 5 via the Unleash config) to force a
   * short diagnostic for QA.
   */
  universalQ: number;
  /** Trigger bar for the first gate window (q `firstGateQ`..`secondGateQ`-1). */
  thetaEarly: number;
  /** Trigger bar for the second gate window (q `secondGateQ`..`universalQ`-1). */
  thetaLate: number;
  /** Assumed ability-estimate change per answered question; sets the drift-based recheck interval. */
  driftPerQ: number;
  /** Floor on the recheck interval (never "check again in 0 questions"). */
  minRecheck: number;
  /** Fallback recheck interval when the ability estimate is unavailable at a check. */
  missingThetaRecheck: number;
}

export const DG_END_EARLY_DEFAULTS: DgEndEarlyConfig = {
  firstGateQ: 18,
  secondGateQ: 27,
  universalQ: 35,
  thetaEarly: -2,
  thetaLate: -1,
  driftPerQ: 0.1,
  minRecheck: 1,
  missingThetaRecheck: 3,
};

/** Unleash flag whose JSON variant payload can override any {@link DgEndEarlyConfig} key at runtime. */
export const DG_END_EARLY_FLAG = 'TEAMB-3338-dg-end-early';

/**
 * Applies a partial override (e.g. an Unleash variant payload) over the defaults. Missing or
 * non-numeric values are ignored, so a malformed flag can never break the diagnostic.
 */
export const mergeDgEndEarlyConfig = (
  defaults: DgEndEarlyConfig,
  overrides: Partial<Record<keyof DgEndEarlyConfig, unknown>> | null | undefined,
): DgEndEarlyConfig => {
  if (!overrides) return defaults;
  const merged = { ...defaults };
  (Object.keys(defaults) as (keyof DgEndEarlyConfig)[]).forEach(key => {
    const raw = overrides[key];
    const value = typeof raw === 'string' ? Number(raw) : raw;
    if (typeof value === 'number' && Number.isFinite(value)) {
      merged[key] = value;
    }
  });
  return merged;
};

/** Parses an Unleash variant JSON payload into a partial config, tolerating any malformed input. */
export const parseConfigOverride = (payloadValue: string | undefined): Partial<DgEndEarlyConfig> | null => {
  if (!payloadValue) return null;
  try {
    const parsed = JSON.parse(payloadValue);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Partial<DgEndEarlyConfig>) : null;
  } catch {
    return null;
  }
};

/** Effective config = defaults overridden by the Unleash variant payload (if the flag is enabled). */
export const useDgEndEarlyConfig = (): DgEndEarlyConfig => {
  const variant = useVariant(DG_END_EARLY_FLAG);
  const override = variant?.enabled ? parseConfigOverride(variant.payload?.value) : null;
  return mergeDgEndEarlyConfig(DG_END_EARLY_DEFAULTS, override);
};

// ---- Pure cadence helpers (config passed in → fully unit-testable) ----

/** The ability-estimate bar in effect at question `q`. */
export const activeThreshold = (q: number, cfg: DgEndEarlyConfig): number =>
  q >= cfg.secondGateQ ? cfg.thetaLate : cfg.thetaEarly;

/** True if the end-early prompt should fire at question `q` for ability estimate `theta`. */
export const shouldPrompt = (q: number, theta: number, cfg: DgEndEarlyConfig): boolean =>
  theta < activeThreshold(q, cfg);

/** A check never overshoots the next mandatory gate (`secondGateQ`, then `universalQ`). */
const capForQuestion = (q: number, cfg: DgEndEarlyConfig): number =>
  q < cfg.secondGateQ ? cfg.secondGateQ : cfg.universalQ;

// Nudge exact .5 boundaries (e.g. distance 0.25 → 2.5 steps) up despite floating-point error,
// so ties consistently round to the longer interval as agreed.
const HALF_UP_EPSILON = 1e-9;

/**
 * Drift-based recheck: the closer `theta` is to the bar, the sooner the next check. Floored at
 * `minRecheck` and capped at the next gate. e.g. (bar −2) θ=−1.9→+1, −1.85→+2, −1.7→+3.
 */
export const nextCheckAt = (q: number, theta: number, cfg: DgEndEarlyConfig): number => {
  const bar = activeThreshold(q, cfg);
  const steps = Math.round(Math.abs(bar - theta) / cfg.driftPerQ + HALF_UP_EPSILON);
  const interval = Math.max(cfg.minRecheck, steps);
  return Math.min(q + interval, capForQuestion(q, cfg));
};

/** Fallback recheck when the ability estimate is unavailable at a check. */
export const nextCheckAtWhenThetaMissing = (q: number, cfg: DgEndEarlyConfig): number =>
  Math.min(q + cfg.missingThetaRecheck, capForQuestion(q, cfg));

/** Which gate a question falls in (drives the log's `gate` label and the active bar). */
export const gateFor = (q: number, cfg: DgEndEarlyConfig): 'early' | 'late' | 'universal' =>
  q >= cfg.universalQ ? 'universal' : q >= cfg.secondGateQ ? 'late' : 'early';

/** The outcome of a check: show the prompt (with its gate), or schedule the next check. */
export type EndEarlyDecision =
  | { kind: 'show'; gate: 'early' | 'late' | 'universal' }
  | { kind: 'schedule'; nextCheckAt: number };

/**
 * Decides, at a check point, whether to show the end-early prompt now or when to check again.
 * `theta` may be null when the estimate is unavailable.
 *  - `q >= universalQ` → always show (universal catch-all; θ is ignored here).
 *  - θ null → schedule via the missing-θ fallback (degrade-safe).
 *  - θ below the active bar → show (early/late gate); otherwise schedule via the drift interval.
 */
export const decideEndEarly = (q: number, theta: number | null, cfg: DgEndEarlyConfig): EndEarlyDecision => {
  if (q >= cfg.universalQ) return { kind: 'show', gate: 'universal' };
  if (theta === null) return { kind: 'schedule', nextCheckAt: nextCheckAtWhenThetaMissing(q, cfg) };
  if (shouldPrompt(q, theta, cfg)) return { kind: 'show', gate: gateFor(q, cfg) };
  return { kind: 'schedule', nextCheckAt: nextCheckAt(q, theta, cfg) };
};
